import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    useDeleteSurveyByIdMutation,
    useGetResponsesBySurveyIdQuery,
    useGetSurveyByIdQuery,
    useCreateResponseMutation,
    useGetEnvironmentQuery, useCreateDaylightProjectOnSurveyMutation,
} from "../../../store/api/formbricks/survey";
import { Button, Card, Col, Row, Space, Spin, Statistic, Popover, message } from "antd";
import {
    openSurveyPreview,
    surveyStatuses,
    transformResponsesData,
    createSurveyResposne,
    getQuestionsFromSurvey,
} from "../../../utils/surveys";
import { CreateEditSurveyModal } from "../CreateEditSurveyModal/CreateEditSurveyModal";
import { LeftCircleOutlined } from "@ant-design/icons";
import QuestionResponses from "./QuestionResponses/QuestionResponses";
import { useExportResponsesMutation } from "../../../store/api/lumi/projects";
import { ProjectsHeader } from "../../Projects/ProjectsHeader";
import { copySurveyLink } from "../../../utils/surveys";
import { downloadObjectAsJson } from "../../../utils/appUtils";

export const SurveyDashboard = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const navigate = useNavigate();
    const { id } = useParams();
    const { data: survey, isFetching: isSurveyLoading } = useGetSurveyByIdQuery(
        id,
        {
            skip: !id,
        }
    );
    const { data: responses, isFetching: isResponsesLoading } =
        useGetResponsesBySurveyIdQuery(id, {
            skip: !id,
        });
    const { data: formbricksEnvironment, isFetching: isEnvironmentLoading } =
        useGetEnvironmentQuery();

    const [questions, setQuestions] = useState([]);

    const getResponsesCount = () => responses?.length;
    const projectId = survey?.name.split("---")[1];
    const getFinishedResponsesPercentage = () =>
        (responses?.filter((res) => res.finished === true).length * 100) /
        getResponsesCount();

    const [isEditSurveyModalOpen, setIsEditSurveyModalOpen] = useState(false);

    const [deleteSurvey] = useDeleteSurveyByIdMutation();
    const [exportResponses, { isLoading: isExporting, data: exportData }] =
        useExportResponsesMutation();
    const [createResponse, { isLoading: isResponsesCreating }] =
        useCreateResponseMutation();
    const [createProject, { isLoading: isProjectCreating }] =
        useCreateDaylightProjectOnSurveyMutation();

    const isLoading =
        isProjectCreating ||
        isSurveyLoading ||
        isResponsesLoading ||
        isEnvironmentLoading ||
        isResponsesCreating;

    const copySurveyLinkHandler = () => {
        copySurveyLink(id);
    };

    const editSurveyHandler = () => {
        setIsEditSurveyModalOpen(true);
    };

    const deleteSurveyHandler = async () => {
        await deleteSurvey(id).unwrap();
        navigate("/survey");
    };

    const exportSurveyHandler = () => {
        downloadObjectAsJson(survey, survey.name);
    };

    const goToPreviewHandler = () => {
        openSurveyPreview(id);
    };

    const goBackHandler = () => {
        navigate(-1);
    };

    const exportResponsesHandler = async () => {
        await exportResponses({
            data: {
                fetch_type: "export_survey_responses",
                fetch_config: {
                    survey_id: id,
                },
                repeat: true,
                repeat_interval: "day",
            },
            projectId,
        });
    };

    const createBulkResponsesHandler = async () => {
        const arr = Array(100).fill(0);
        const promises = arr.map((el) => {
            const data = {};
            getQuestionsFromSurvey(survey).forEach(
                (question) =>
                    (data[question?.id] = createSurveyResposne(question))
            );
            createResponse({
                environmentId: formbricksEnvironment?.id,
                body: {
                    surveyId: survey.id,
                    personId: null,
                    finished: true,
                    data,
                },
            }).unwrap();
        });
        await Promise.all(promises);
    };

    const createProjectHandler = async () => {
        try {
            await createProject({ surveyId: id });
            messageApi.open({
                type: "success",
                content: "Project created successfully!",
                duration: 3,
            });
        } catch (e) {
            messageApi.open({
                type: "error",
                content: "Error creating project!",
                duration: 3,
            });
        }
    };

    useEffect(() => {
        if (survey && responses?.length) {
            setQuestions(
                transformResponsesData(
                    responses,
                    getQuestionsFromSurvey(survey)
                )
            );
        }
    }, [survey, responses?.length]);

    return (
        <>
            <CreateEditSurveyModal
                isOpen={isEditSurveyModalOpen}
                onClose={() => setIsEditSurveyModalOpen(false)}
                surveyId={id}
            />
            <div>
                <ProjectsHeader />
                <div style={{ padding: "5%" }}>
                    <Button
                        style={{ marginBottom: "20px" }}
                        icon={<LeftCircleOutlined />}
                        onClick={goBackHandler}
                    >
                        Back
                    </Button>
                    <Spin spinning={isLoading} tip="Loading">
                        <Card
                            title={survey?.name.split("---")[0]}
                            type="inner"
                            extra={
                                <CardTitleExtra
                                    onCreateBulkResponses={
                                        createBulkResponsesHandler
                                    }
                                    onCopy={copySurveyLinkHandler}
                                    onEdit={editSurveyHandler}
                                    onDelete={deleteSurveyHandler}
                                    onPreview={goToPreviewHandler}
                                    onExport={exportResponsesHandler}
                                    isExporting={isExporting}
                                    isExportSuccess={
                                        exportData === "Task has started"
                                    }
                                    onExportSurvey={exportSurveyHandler}
                                    onCreateProject={createProjectHandler}
                                />
                            }
                        >
                            <Row
                                gutter={[16, 16]}
                                style={{ textAlign: "center" }}
                            >
                                <Col span={6}>
                                    <Card
                                        bordered={true}
                                        style={{ background: "#fafafa" }}
                                    >
                                        <Statistic
                                            title="Responses"
                                            value={getResponsesCount()}
                                            valueStyle={{ color: "#1677ff" }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card
                                        bordered={true}
                                        style={{ background: "#fafafa" }}
                                    >
                                        <Statistic
                                            title="Status"
                                            value={
                                                surveyStatuses.find(
                                                    (status) =>
                                                        status.value ===
                                                        survey?.status
                                                )?.label
                                            }
                                            valueStyle={{ color: "#1677ff" }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card
                                        bordered={true}
                                        style={{ background: "#fafafa" }}
                                    >
                                        <Statistic
                                            title="Finished responses"
                                            value={`${parseInt(
                                                getFinishedResponsesPercentage() ||
                                                    0
                                            )}%`}
                                            valueStyle={{ color: "#1677ff" }}
                                        />
                                    </Card>
                                </Col>
                                <Col span={6}>
                                    <Card
                                        bordered={true}
                                        style={{ background: "#fafafa" }}
                                    >
                                        <Statistic
                                            title="Counter of export to project"
                                            value={survey?.projectExportCounter}
                                            valueStyle={{ color: "#1677ff" }}
                                        />
                                    </Card>
                                </Col>
                                {questions &&
                                    questions.map((question) => (
                                        <QuestionResponses
                                            key={question.id}
                                            question={question}
                                        />
                                    ))}
                            </Row>
                        </Card>
                    </Spin>
                </div>
            </div>
        </>
    );
};

function CardTitleExtra({
    onEdit,
    onDelete,
    onPreview,
    onCopy,
    onCreateBulkResponses,
    onExportSurvey,
    onCreateProject,
}) {
    return (
        <Space>
            <Button onClick={onCreateBulkResponses} danger>
                Create bulk responses
            </Button>
            <Popover content="Copied to clipboard" trigger="click">
                <Button type="dashed" onClick={onCopy}>
                    Copy link
                </Button>
            </Popover>
            <Button onClick={onPreview}>Preview</Button>
            <Button onClick={onExportSurvey}>Export as JSON</Button>
            <Button onClick={onCreateProject}>Create Daylight project</Button>
            <Button type="primary" onClick={onEdit}>
                Edit
            </Button>
            <Button type="primary" onClick={onDelete} danger>
                Delete
            </Button>
        </Space>
    );
}

export default SurveyDashboard;
